// Free, self-built device fingerprint - no paid fingerprinting service (FingerprintJS Pro etc).
// Combines several browser/hardware signals that are stable across page reloads on the SAME
// device/browser (canvas render, WebGL renderer string, screen, timezone, UA, core count) into
// one SHA-256 hash. This is "good enough" to stop casual multi-accounting from the same phone/
// browser; a determined person spoofing everything at once (different browser + VPN + spoofed
// canvas) can still get a different hash - there is no free client-side signal that defeats that.
//
// Cached in localStorage so we don't recompute the canvas/WebGL probes on every call.

const CACHE_KEY = 'animora_device_hash_v2' // v2: random per-install id (v1 collided between phones of the same model)

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

function canvasSignal(): string {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 220
    canvas.height = 40
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    ctx.textBaseline = 'top'
    ctx.font = '16px "Arial"'
    ctx.fillStyle = '#f60'
    ctx.fillRect(0, 0, 100, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('Animora-fp:@/!$%^&*()', 2, 12)
    ctx.strokeStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.beginPath()
    ctx.arc(50, 20, 15, 0, Math.PI * 2)
    ctx.stroke()
    return canvas.toDataURL()
  } catch {
    return ''
  }
}

function webglSignal(): string {
  try {
    const canvas = document.createElement('canvas')
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null
    if (!gl) return ''
    const dbg = gl.getExtension('WEBGL_debug_renderer_info')
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR)
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)
    return `${vendor}::${renderer}`
  } catch {
    return ''
  }
}

/** Random 64-hex id (works even where crypto.subtle is missing, e.g. some old WebViews). */
function randomHex64(): string {
  try {
    const b = new Uint8Array(32)
    crypto.getRandomValues(b)
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  } catch {
    let out = ''
    for (let i = 0; i < 64; i++) out += Math.floor(Math.random() * 16).toString(16)
    return out
  }
}

/**
 * Returns this INSTALL's id (a random 64-hex value created once and kept in localStorage).
 *
 * v2 FIX: the old version hashed only phone traits (user agent, screen size, GPU, timezone...). Thousands of
 * phones of the same model + WebView version produced the SAME hash, so they all shared ONE "device": one
 * under-13 attempt (ageLocks) or the 3-6 signups/day cap (guard) then blocked/banned every new account on that
 * phone model ("account banta / login hota nahi kisi kisi device me"). A random per-install id cannot collide.
 * (Clearing app data gives a new id; the IP cap in /api/account/device stays as the backstop.)
 */
export async function getDeviceHash(): Promise<string> {
  if (typeof window === 'undefined') return ''
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (cached && /^[0-9a-f]{64}$/.test(cached)) return cached
  } catch { /* ignore */ }

  let hash = ''
  try {
    // Mix a fresh random value into the hash so it is unique even if two phones share every trait.
    const nav = navigator as any
    const parts = [
      randomHex64(),
      navigator.userAgent,
      navigator.language,
      String(navigator.hardwareConcurrency || ''),
      String(nav.deviceMemory || ''),
      `${screen.width}x${screen.height}`,
      canvasSignal(),
      webglSignal(),
    ]
    hash = await sha256Hex(parts.join('|'))
  } catch {
    hash = randomHex64()
  }
  try { localStorage.setItem(CACHE_KEY, hash) } catch { /* ignore */ }
  return hash
}
